import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { deleteThread } from "../api"
import type { CaseFileListRow, FeatureTag, RecentReading, ThreadRow } from "../contract"
import { featureOf, type Feature } from "../matter/features"
import { track } from "@/lib/analytics"
import * as oioSim from "../../oio-sim/api"
import * as oiaSim from "../../oia-sim/api"

/** How many Recents the drawer lists. */
const RECENTS = 15

/**
 * The feature whose Recents these are. Research lists Nira threads; OIO and OIA list the order
 * simulator's drafts. Notice and appeal sessions live in their workspace, so on those pages the
 * drawer shows the general (research) Recents.
 */
export type HistoryScope = Feature

type Kind = "oio" | "oia" | "nira"

/** One session: a Nira thread or a simulator draft. */
interface Session { kind: "thread" | "sim" | "reading"; type: Kind; cat: Cat; id: string; title: string; to: string; when?: string | null; current: boolean }

/** The drawer's filter: every flow, or one of them. */
type Tab = "all" | Cat
type Cat = "research" | "notice" | "appeal" | "draft"
const TABS: { id: Tab; label: string; empty: string }[] = [
  { id: "all", label: "All", empty: "Your recent sessions will show here." },
  { id: "research", label: "Research", empty: "No research yet." },
  { id: "notice", label: "Notices", empty: "No notice readings yet." },
  { id: "appeal", label: "Appeals", empty: "No appeal readings yet." },
  { id: "draft", label: "Drafts", empty: "No order drafts yet." },
]
const CAT_LABEL: Record<Cat, string> = { research: "Research", notice: "Notice", appeal: "Appeal", draft: "Draft" }

/** Where one reading opens: its workspace's reading page. */
export const readingPath = (r: Pick<RecentReading, "case_id" | "id">): string => `/workspace/${encodeURIComponent(r.case_id)}/reading/${encodeURIComponent(r.id)}`

/** How the notice and appeal readings load: shown as a note, never blocking the rest of the list. */
export type ReadingsState = "loading" | "ready" | "error"

/** One order the simulator drafted: an Order-in-Original or an Order-in-Appeal. */
export interface SimDraft { kind: "oio" | "oia"; id: string; title: string; updated_at: string; case_id?: string | null; active?: boolean; feature?: FeatureTag | null }

/** The simulators' drafts for the kinds asked, newest first. A simulator that does not answer is left out, never an error. */
export async function loadSimDrafts(limit = 20, kinds: SimDraft["kind"][] = ["oio", "oia"]): Promise<SimDraft[]> {
  const [a, b] = await Promise.all([
    kinds.includes("oio") ? Promise.resolve(oioSim.listThreads(limit)).catch(() => []) : Promise.resolve([]),
    kinds.includes("oia") ? Promise.resolve(oiaSim.listThreads(limit)).catch(() => []) : Promise.resolve([]),
  ])
  const rows = [
    ...(Array.isArray(a) ? a : []).map((r) => ({ kind: "oio" as const, id: r.id, title: r.title, updated_at: r.updated_at, case_id: r.case_id, active: r.active, feature: r.feature })),
    ...(Array.isArray(b) ? b : []).map((r) => ({ kind: "oia" as const, id: r.id, title: r.title, updated_at: r.updated_at, case_id: r.case_id, active: r.active, feature: r.feature })),
  ]
  return rows.sort((x, y) => y.updated_at.localeCompare(x.updated_at))
}

/** Where a workspace opens: its latest finished reading, else the workspace page. */
export function matterPath(m: CaseFileListRow): string {
  const r = m.latest_reading
  const base = `/workspace/${encodeURIComponent(m.id)}`
  return r && r.status !== "failed" ? `${base}/reading/${encodeURIComponent(r.id)}` : base
}

/** A workspace is an appeal when it began with an order, or its latest reading is one. */
export function isAppeal(m: CaseFileListRow): boolean {
  return m.lead === "order" || m.latest_reading?.kind === "order_preview" || m.latest_reading?.kind === "appeal"
}

const TRASH = <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 6h12M8 6V4h4v2M6 6l1 10h6l1-10" /></svg>

/**
 * The drawer's Recents, Claude-style: only general sessions, the ones that belong to no workspace
 * (a thread with a `case`, a draft with a `case_id`, is a workspace session and is reached from its
 * workspace page). Newest first, capped. Each row deletes in place, after a second, named tap.
 */
export function NiraHistory({ threads, sims = [], readings = [], readingsState = "ready", onRetry, threadId, onClose, onGone, ago }: {
  threads: ThreadRow[]
  /** The order simulator's drafts (OIO and OIA). */
  sims?: SimDraft[]
  /** Notice and appeal readings across the reader's workspaces. */
  readings?: RecentReading[]
  readingsState?: ReadingsState
  /** Try the readings again after an error. */
  onRetry?: () => void
  /** Accepted for callers (the New button keys off it); the list itself covers every flow. */
  scope?: HistoryScope
  threadId: string
  onClose: () => void
  /** A row was deleted: the parent drops it from its lists. */
  onGone: (kind: "thread" | "sim", id: string) => void
  ago: (iso: string) => string
}) {
  const navigate = useNavigate()
  const [asking, setAsking] = useState("")
  const [busy, setBusy] = useState("")
  const [said, setSaid] = useState("")
  const [tab, setTab] = useState<Tab>("all")

  // A thread nobody has asked anything in (no title yet) is not a session; the open one still shows.
  // A draft nobody has touched (untitled, nothing uploaded, no turn) is not a session either.
  // A thread inside a workspace is reached from its workspace page; the workspace's readings are listed below.
  const time = (x: { when?: string | null }) => Date.parse(x.when ?? "") || 0
  const all: Session[] = [
    ...threads.filter((t) => !t.case && (!!t.title?.trim() || t.id === threadId) && featureOf({ feature: t.feature, inferred: "research" }) === "research").map((t): Session => ({
      kind: "thread", type: "nira", cat: "research", id: t.id, title: t.title || "New research", to: `/nira/${encodeURIComponent(t.id)}`, when: t.updated_at, current: t.id === threadId,
    })),
    ...readings.filter((r) => r.status !== "failed").map((r): Session => ({
      kind: "reading", type: "nira", cat: featureOf({ feature: r.feature }) === "notice" ? "notice" : "appeal", id: r.id, title: r.title || "Untitled workspace", to: readingPath(r), when: r.updated_at, current: false,
    })),
    ...sims.filter((d) => d.active !== false || d.id === threadId).map((d): Session => ({
      kind: "sim", type: d.kind, cat: "draft", id: d.id, title: d.title && d.title !== "New simulation" ? d.title : "Untitled draft", to: `/${d.kind}-sim/${encodeURIComponent(d.id)}`, when: d.updated_at, current: d.id === threadId,
    })),
  ].sort((a, b) => time(b) - time(a))
  const recents = all.filter((x) => tab === "all" || x.cat === tab).slice(0, RECENTS)
  const wantsReadings = tab === "all" || tab === "notice" || tab === "appeal"

  const remove = async (kind: "thread" | "sim", id: string, type: Kind, current: boolean) => {
    setBusy(id)
    setSaid("")
    try {
      if (kind === "thread") await deleteThread(id)
      else await (type === "oio" ? oioSim : oiaSim).deleteThread(id)
      onGone(kind, id)
      track("history_item_deleted", { kind })
      setAsking("")
      if (current) {
        onClose()
        navigate("/ask")
      }
    } catch (err) {
      setSaid((err as { status?: number })?.status === 409
        ? (kind === "sim" ? "That draft is still being written. Delete it once it finishes." : "That answer is still being written. Delete it once it finishes.")
        : "Couldn't delete that. Try again.")
    } finally {
      setBusy("")
    }
  }

  const confirm = (label: string, text: string, id: string, run: () => void) => (
    <div className="hconfirm" role="group" aria-label={`Delete ${label}`}>
      <span>{text}</span>
      <button type="button" className="hc-del" disabled={busy === id} onClick={run}>{busy === id ? "Deleting…" : "Delete"}</button>
      <button type="button" className="hc-keep" onClick={() => setAsking("")}>Keep</button>
    </div>
  )

  const sessionRow = (x: Session) => (
    <li key={`${x.kind}:${x.id}`} className={asking === x.id ? "asking" : undefined} data-cat={x.cat}>
      {asking === x.id && x.kind !== "reading" ? confirm(x.title, x.kind === "sim" ? "Delete this draft and its decisions?" : "Delete this research?", x.id, () => void remove(x.kind as "thread" | "sim", x.id, x.type, x.current)) : (
        <>
          <button type="button" className="hi" aria-current={x.current ? "true" : undefined} title={x.title} onClick={() => { onClose(); navigate(x.to) }}>
            <span className="hi-t">{x.title}</span>
            <span className="hi-k">{CAT_LABEL[x.cat]}</span>
            {x.when ? <span className="hi-d">{ago(x.when)}</span> : null}
          </button>
          {x.kind === "reading" ? null : <button type="button" className="hi-x" aria-label={`Delete ${x.title}`} onClick={() => { setSaid(""); setAsking(x.id) }}>{TRASH}</button>}
        </>
      )}
    </li>
  )

  return (
    <>
      {said ? <p className="hempty" role="alert">{said}</p> : null}
      <section className="hrecent" aria-label="Recents">
        <h2>Recents</h2>
        <div className="hseg" role="group" aria-label="Filter recents">
          {TABS.map((t) => (
            <button key={t.id} type="button" aria-pressed={tab === t.id} className="hseg-b" onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </div>
        <div>
          {recents.length ? <ul>{recents.map(sessionRow)}</ul> : readingsState === "loading" && wantsReadings ? <p className="hempty">Loading…</p> : <p className="hempty">{TABS.find((t) => t.id === tab)?.empty}</p>}
          {readingsState === "error" && wantsReadings ? (
            <p className="hempty" role="alert">
              Couldn't load your notice and appeal readings.
              {onRetry ? <> <button type="button" className="hseg-retry" onClick={onRetry}>Try again</button></> : null}
            </p>
          ) : null}
        </div>
      </section>
    </>
  )
}
